import { AdapterType } from '@k-ucap/shared';
import type { MessageInstance, AdapterStats, AdapterStatus } from '@k-ucap/shared';
import type { MessageTransportAdapter } from './MessageTransportAdapter.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';
import { firebaseApp } from '@/infrastructure/firebase/firebaseApp.js';
import { getAuth } from 'firebase/auth';

export interface WebSocketAdapterConfig {
  url: string;
  projectId: string;
  name?: string;
  token?: string;
  heartbeatMs?: number;
  reconnectIntervalMs?: number;
  maxReconnects?: number;
}

export interface MessageEnvelope {
  id: string;
  timestamp: string;
  source: string;
  target: string;
  messageType: number;
  payload: Record<string, unknown>;
  rawHex?: string;
  metadata?: Record<string, unknown>;
  projectId?: string;
}

export class WebSocketMessageAdapter implements MessageTransportAdapter {
  private ws: WebSocket | null = null;
  private status: AdapterStatus = 'idle';
  private stats: AdapterStats = {
    rxCount: 0,
    txCount: 0,
    rxBytes: 0,
    txBytes: 0,
    errors: 0,
    reconnects: 0,
  };
  private handlers: Set<(message: MessageInstance) => void> = new Set();
  private statusHandlers: Set<(status: AdapterStatus, error?: string) => void> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempt = 0;
  private authFailed = false;
  private manualDisconnect = false;
  private readonly config: Required<WebSocketAdapterConfig> & { name: string };

  constructor(config: WebSocketAdapterConfig) {
    this.config = {
      url: config.url,
      projectId: config.projectId,
      name: config.name ?? `WebSocketMessageAdapter(${config.url})`,
      token: config.token ?? '',
      heartbeatMs: config.heartbeatMs ?? 30000,
      reconnectIntervalMs: config.reconnectIntervalMs ?? 3000,
      maxReconnects: config.maxReconnects ?? 10,
    };
    useIntegrationStore.getState().registerAdapter(this.getName(), this.getName(), AdapterType.WEBSOCKET);
  }

  getName(): string {
    return this.config.name;
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.clearReconnectTimer();
    this.setStatus('connecting');
    try {
      this.ws = new WebSocket(this.config.url);
      this.ws.onopen = () => this.handleOpen();
      this.ws.onmessage = (event) => this.handleMessage(event.data);
      this.ws.onerror = (event) => this.handleError(event);
      this.ws.onclose = () => this.handleClose();
    } catch (err) {
      this.handleError(err);
    }
  }

  async disconnect(): Promise<void> {
    this.manualDisconnect = true;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    if (this.status !== 'error') {
      this.setStatus('disconnected');
    }
    this.manualDisconnect = false;
  }

  async publish(message: MessageInstance): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.stats.errors++;
      useIntegrationStore.getState().updateAdapterStats(this.getName(), { ...this.stats });
      throw new Error('WebSocket not connected');
    }
    const envelope: MessageEnvelope = {
      id: message.id,
      timestamp: message.timestamp,
      source: message.source,
      target: message.target,
      messageType: message.messageType,
      payload: message.fields,
      rawHex: message.rawBytes ? bytesToHex(message.rawBytes) : undefined,
      metadata: {
        projectId: message.projectId ?? this.config.projectId,
        environment: message.environment,
        sequenceNumber: message.sequenceNumber,
        runId: message.runId,
      },
    };
    const data = JSON.stringify(envelope);
    this.ws.send(data);
    this.stats.txCount++;
    this.stats.txBytes += data.length;
    useIntegrationStore.getState().updateAdapterStats(this.getName(), { ...this.stats });
    useIntegrationStore.getState().recordMessage(this.getName(), 'TX', message, 'ok');
  }

  subscribe(handler: (message: MessageInstance) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  getStatus(): AdapterStatus {
    return this.status;
  }

  getStats(): AdapterStats {
    return { ...this.stats };
  }

  onStatusChange(callback: (status: AdapterStatus, error?: string) => void): () => void {
    this.statusHandlers.add(callback);
    return () => this.statusHandlers.delete(callback);
  }

  private setStatus(status: AdapterStatus, error?: string): void {
    this.status = status;
    if (status === 'connected') {
      this.stats.lastConnectedAt = new Date().toISOString();
    } else if (status === 'disconnected' || status === 'error') {
      this.stats.lastDisconnectedAt = new Date().toISOString();
    }
    if (error) {
      this.stats.lastError = error;
    }
    useIntegrationStore.getState().updateAdapterStatus(this.getName(), status, error);
    useIntegrationStore.getState().updateAdapterStats(this.getName(), { ...this.stats });
    this.statusHandlers.forEach((h) => h(status, error));
  }

  private handleOpen(): void {
    this.reconnectAttempt = 0;
    this.authFailed = false;
    this.sendAuth().catch(() => {
      // ignore
    });
    this.startHeartbeat();
  }

  private handleMessage(data: string): void {
    let parsed: Record<string, unknown> | undefined;
    try {
      parsed = JSON.parse(data) as Record<string, unknown>;
    } catch {
      this.stats.errors++;
      return;
    }

    const type = parsed.type;
    if (type === 'auth_ok') {
      this.setStatus('connected');
      return;
    }
    if (type === 'auth_error') {
      this.authFailed = true;
      const reason = typeof parsed.reason === 'string' ? parsed.reason : 'Authentication failed';
      this.setStatus('error', reason);
      this.disconnect().catch(() => {
        // ignore
      });
      return;
    }
    if (type === 'ping' || type === 'pong') {
      return;
    }

    try {
      const envelope = parsed as unknown as MessageEnvelope;
      const instance = envelopeToInstance(envelope);
      this.stats.rxCount++;
      this.stats.rxBytes += data.length;
      useIntegrationStore.getState().updateAdapterStats(this.getName(), { ...this.stats });
      useIntegrationStore.getState().recordMessage(this.getName(), 'RX', instance, 'ok');
      this.handlers.forEach((h) => h(instance));
    } catch (err) {
      this.stats.errors++;
      const message = err instanceof Error ? err.message : String(err);
      this.stats.lastError = message;
      useIntegrationStore.getState().updateAdapterStats(this.getName(), { ...this.stats });
    }
  }

  private handleError(event: unknown): void {
    this.stats.errors++;
    const message = event instanceof Error ? event.message : 'WebSocket error';
    this.stats.lastError = message;
    this.setStatus('error', message);
  }

  private handleClose(): void {
    this.stopHeartbeat();
    this.ws = null;
    if (this.manualDisconnect) {
      this.manualDisconnect = false;
      return;
    }
    this.setStatus('disconnected');
    if (this.authFailed || this.reconnectAttempt >= this.config.maxReconnects) {
      this.setStatus('error', this.authFailed ? 'Authentication failed' : 'Max reconnects exceeded');
      return;
    }
    this.setStatus('reconnecting');
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(
      () => {
        this.reconnectAttempt++;
        this.stats.reconnects++;
        this.connect().catch(() => {
          // ignore
        });
      },
      this.config.reconnectIntervalMs * (this.reconnectAttempt + 1)
    );
  }

  private async sendAuth(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    let token = '';
    try {
      if (firebaseApp) {
        const auth = getAuth(firebaseApp);
        const currentUser = auth.currentUser;
        token = (await currentUser?.getIdToken()) ?? '';
      }
    } catch {
      token = '';
    }
    this.ws.send(
      JSON.stringify({
        type: 'auth',
        token,
        projectId: this.config.projectId,
        clientType: 'browser',
      })
    );
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
      }
    }, this.config.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

const bytesToHex = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');

const envelopeToInstance = (envelope: MessageEnvelope): MessageInstance => ({
  id: envelope.id,
  messageType: envelope.messageType,
  standard: (envelope.metadata?.standard as string | undefined) ?? 'EXTERNAL',
  name: (envelope.metadata?.name as string | undefined) ?? `External #${envelope.messageType}`,
  source: envelope.source,
  target: envelope.target,
  direction: 'RX',
  timestamp: envelope.timestamp,
  fields: envelope.payload,
  projectId: envelope.projectId,
  environment: (envelope.metadata?.environment as string | undefined) ?? 'development',
  sequenceNumber: (envelope.metadata?.sequenceNumber as number | undefined) ?? 0,
  runId: envelope.metadata?.runId as string | undefined,
});

export const createWebSocketMessageAdapter = (config: WebSocketAdapterConfig): WebSocketMessageAdapter =>
  new WebSocketMessageAdapter(config);
